import { AdminPageCabecalhoComponent } from '../../shared/admin-page-cabecalho/admin-page-cabecalho.component';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { CreateGrupo, GruposApiService, Permissao, PermissoesApiService } from '@cei-ufg/ngx-cei-seguranca';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  CeiAutocompleteChipsComponent,
  CeiAutocompleteOptionComponent,
  CeiButtonComponent,
  CeiInputTextComponent,
  CeiPanelComponent,
  CeiSnackBarService,
} from '@cei-ufg/ngx-arquitetura-cei-frontend';
import { notifyAdminFormError, notifyAdminFormSuccess, stopIfAdminFormInvalid } from '../../shared/admin-form.utils';
import { forkJoin, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

/** Cadastro de grupo com permissões via autocomplete com chips. */
@Component({
  selector: 'cei-admin-group-form',
  standalone: true,
  imports: [AdminPageCabecalhoComponent, 
    CommonModule,
    FormsModule,
    RouterModule,
    CeiInputTextComponent,
    CeiAutocompleteChipsComponent,
    CeiAutocompleteOptionComponent,
    CeiButtonComponent,
    CeiPanelComponent,
  ],
  templateUrl: './group-form.component.html',
})
export class AdminGroupFormComponent implements OnInit {
  form: Partial<CreateGrupo> = { name: '' };
  availablePermissions: Permissao[] = [];
  permissoesSelecionadas: Permissao[] = [];
  permissoesCatalogoCarregado = false;

  gruposApi = inject(GruposApiService);
  permissoesApi = inject(PermissoesApiService);
  cdr = inject(ChangeDetectorRef);
  private readonly snackBar = inject(CeiSnackBarService);

  /** Rótulo de opção e filtro de busca de permissão (descrição + slug). */
  protected readonly formatarPermissao = (valor: unknown): string => {
    const permissao = valor as Permissao;
    return `${permissao.name} (${permissao.codename})`;
  };

  /** Rótulo do chip de permissão (somente slug/codename). */
  protected readonly formatarPermissaoChip = (valor: unknown): string =>
    (valor as Permissao).codename;

  /** Chave estável para deduplicar permissões selecionadas. */
  protected readonly extrairChavePermissao = (valor: unknown): string =>
    String((valor as Permissao).id);

  ngOnInit(): void {
    this.carregarPermissoesDisponiveis();
  }

  /** Chave de remontagem do autocomplete após limpar o formulário. */
  chaveAutocompletePermissoes(): string {
    const ids = this.permissoesSelecionadas.map((permissao) => permissao.id).join('|');
    return `${this.permissoesCatalogoCarregado}|${ids}`;
  }

  onSubmit(form: NgForm) {
    if (stopIfAdminFormInvalid(form)) {
      this.cdr.markForCheck();
      return;
    }

    this.gruposApi
      .grupoCreate({ createGrupo: this.form as CreateGrupo })
      .pipe(
        switchMap((grupoCriado) => {
          const grupoId = Number(grupoCriado.id);
          if (!grupoId || this.permissoesSelecionadas.length === 0) {
            return of(grupoCriado);
          }

          const associacoes = this.permissoesSelecionadas.map((permissao) =>
            this.gruposApi.grupoAssociarPermissaoCreate({
              id: grupoId,
              associateGrupoPermissao: { permissao: Number(permissao.id) },
            }),
          );

          return forkJoin(associacoes);
        }),
      )
      .subscribe({
        next: () => {
          notifyAdminFormSuccess(this.snackBar, 'Grupo criado com sucesso!');
          this.form = { name: '' };
          this.permissoesSelecionadas = [];
          this.cdr.markForCheck();
        },
        error: (err) => {
          notifyAdminFormError(this.snackBar, 'Erro ao criar grupo.', err);
          this.cdr.markForCheck();
        },
      });
  }

  /** Carrega catálogo de permissões para o autocomplete de chips. */
  private carregarPermissoesDisponiveis(): void {
    this.permissoesApi.permissaoList({}).subscribe({
      next: (data) => {
        this.availablePermissions = data.results ?? [];
        this.permissoesCatalogoCarregado = true;
        this.cdr.markForCheck();
      },
      error: () => {
        this.availablePermissions = [];
        this.permissoesCatalogoCarregado = true;
        this.cdr.markForCheck();
      },
    });
  }
}
